
import { get as _get } from "lodash";
import { detect } from 'detect-browser';
import {CALocation, USLocation} from "lib/workperks/common/constants";

export const getBrowserInfo = detect();

// Set the variable where the server session old values are stored.
const _sessionOldInput = window.s.old;
const _actionBarHeight = null;

export const isEmpty = (json) => {
    if(!json || (json instanceof Array && json.length === 0)) {
        return true;
    }

    for(const prop in json) {
        if(Object.prototype.hasOwnProperty.call(json, prop))
            return false;
    }

    return JSON.stringify(json) === JSON.stringify({});
}

export const toTitleCase = (str: string): string => {
    return str.replace(/\w\S*/g, (txt) => {return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();});
}

export const toRegularForm = (str: string): string => {
    return str
    // insert a space before all caps
    .replace(/([A-Z])/g, ' $1')
    // uppercase the first character
    .replace(/^./, (str) => { return str.toUpperCase(); })
}

const isFunction = obj => {
    return Boolean(obj?.constructor && obj.call && obj.apply);
}

export const setOldValuesForDict = (dict: any, defaultDict: any = null): any => {
    if( (!_sessionOldInput && !defaultDict) || (isEmpty(_sessionOldInput) && isEmpty(defaultDict)) ) {
        return ;
    }

    const fromObject = isEmpty(_sessionOldInput) ? defaultDict : _sessionOldInput;

    for(const property in dict) {
        if(!isFunction(dict[property]) && property in fromObject) {
            dict[property] = fromObject[property];
        }
    }
}

export const navigateToUrl = (url: string) => {
    if(!/^(?!www\.|(?:http|ftp)s?:\/\/|[A-Za-z]:\\|\/\/).*/.test(url)) {
        throw `navigateToUrlError -- Invalid URL given: '${url}'`;
    }

    window.location.href = url;
}

export const createArgumentsFromObj = (payloadObject) => {
    let payload = "";
    if(payloadObject) {
        for(const property in payloadObject){
            payload += "&" + property + "=" + payloadObject[property];
        }
    }

    return payload;
}

export const parseParams = (querystring) => {

    // parse query string
    const params = new URLSearchParams(querystring);
    const obj = {};

    // iterate over all keys
    for (const key of params.keys()) {
        if (params.getAll(key).length > 1) {
            obj[key] = params.getAll(key);
        } else {
            obj[key] = params.get(key);
        }
    }

    return obj;
};

export const scrollIntoView = (element: Element) => {
    if(!_actionBarHeight) {
        const _actionBarElement = document.getElementById("action-nav");
        const _actionBarHeight = _actionBarElement ? _actionBarElement.getBoundingClientRect().height : 0;
    }

    const elementPosition = element.getBoundingClientRect().top + window.scrollY;
    const offsetPosition = elementPosition - _actionBarHeight;

    window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
    });
}

export const hideWizardTip = () => {
    document.getElementById("wizard__tip").style.display = "none";
    document.getElementById("wizard__tip").className = "";
    document.getElementById("wizard__content").className = "col-xl-8";
};

export const showWizardTip = () => {
    document.getElementById("wizard__tip").style.display = "block";
    document.getElementById("wizard__tip").className = 'd-none d-lg-block col-4 mb-3';
    if (document.getElementById("wizard__content")) {
        document.getElementById("wizard__content").className = "col-xl-8";
    }
};

export const __ = (key, bindings = null) => {
    if(!window.l) {
        throw "No localization in window variable.";
    }

    let translation = _get(window.l, key);

    if(translation && bindings) {
        for(const key in bindings) {
            const value = bindings[key];
            translation = translation.replace(":" + key, value);
        }
    }

    return translation;
};

export const resourceExists = (url) => {

    const http = new XMLHttpRequest();

    try {
        http.open('HEAD', url, false);
        http.send();
    } catch (exception) {
        console.warn(`Utilities.resourceExists: '${url}' does not exist.`);
        return false;
    }

    let size = 1000;

    if (http.status !== 404 && http.status !== 403) {
        try {
            size = parseInt(http.getResponseHeader("Content-Length"));
        } catch (exception) {
            return 1000;
        }

        return size;
    }

    return false;
};

export const getBrowserLocation = (
        callback: PositionCallback,
        errorCallback: PositionErrorCallback = null
    ) => {
    if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(callback, errorCallback);
        return true;
    }

    return false;
}

export const asset = (path) => {
    if(!window.v.cdn) {
        return "//www.venngo.com/" + path;
    }

    if(window.v.cdn[window.v.cdn.length - 1] === "/") {
        window.v.cdn = window.v.cdn.slice(0, -1)
    }

    return window.v.cdn + path;
};

export const route = (path, parameters = null) => {
    if(!window.s.route) {
        throw Error("No window.s.route defined.");
    }

    let route = window.s.route[path];

    if(route && parameters) {
        for(const key in parameters) {
            const value = parameters[key];
            route = route.replace(":" + key, value);
        }
    }

    return route;
}

export const randomElementsFromArray = (collection, number = 1) => {
    const newArray = [];
    if(number < collection.length){
        let count = 0;
        while(count < number){
            const randomElement = collection[Math.floor(Math.random() * collection.length)];
            if(newArray.indexOf(randomElement) === -1){
                newArray.push(randomElement);
                count++;
            }
        }
        return newArray.length > 1 ? newArray : newArray[0];
    }
    return collection;
}

export const localizeCollection = (collection, locale = null) => {
    return collection.map(element => {
        for(const property in element) {
            if(element[property] && (element[property][locale] || Object.prototype.hasOwnProperty.call(element[property], locale))) {
                element[property] = element[property][locale];
            }
        }
        return element;
    });
}

export const isNumber = (maybeNumber) => {
    // + is a unary operator shorthand for Number(x).
    // Number(x) returns Nan if x is not a number.
    return !isNaN(+maybeNumber);
}

export const fetchSourceLocation = (source) => {
    /*
    * We are using this for notifications only for now. If the user is on US Location and tries to land on
    * a CA Location Perk we will check if we have a stored CA location for this user otherwise assign Venngo HQ location
    * so the user is not blocked and vice versa for a US location we will assign Apple HQ if user does not have a
    * US location stored
    */
    const country = source === 'ad' ? 'US' : 'CA';

    let primaryLocation = window.s.user.locations.find(location => location.country === country);

    if (!primaryLocation) {
        primaryLocation = country === 'US' ? USLocation : CALocation;
    }

    return primaryLocation;
}
